import { getErrorMessage } from '@/utils/errors';
import {
  Box,
  CodeBlock,
  Flex,
  Heading,
  Icon,
  Link,
  Modal,
  ModalBody,
  Paragraph,
  useModal,
} from '@customerio/pluma-components/react';
import type { PlumaManagedModalProps } from '@customerio/pluma-components/react';
import type { FallbackProps } from 'react-error-boundary';

type ErrorDetailsModalData = {
  /** The error message shown in the details code block. */
  message: string;
};

function ErrorDetailsModal({ modalState, data, onClose }: PlumaManagedModalProps<ErrorDetailsModalData>) {
  return (
    <Modal state={modalState} onClose={() => onClose()} title="Error details" size="md">
      <ModalBody>
        <CodeBlock code={data.message} language="plain" withCopyButton={true} withLineNumbers={false} />
      </ModalBody>
    </Modal>
  );
}

/**
 * Default fallback UI for `AppErrorBoundary`.
 * Shows a left-aligned inline error with retry and details modal.
 */
export const AppErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
  const errorDetailsModal = useModal(ErrorDetailsModal);

  return (
    <Box p="300">
      <Flex gap="075" alignItems="center" mb="050">
        <Icon name="critical" size="md" />
        <Heading level="3">Something went wrong</Heading>
      </Flex>
      <Flex direction="column" gap="025">
        <Paragraph color="subtle">
          Our team has been notified. Please try again or contact support if the issue persists.
        </Paragraph>
      </Flex>
      <Flex mt="075" gap="100">
        <Link onClick={resetErrorBoundary}>Try again</Link>
        <Link
          icon="chevron-right"
          iconPosition="trailing"
          onClick={() => {
            // Close with the fallback (e.g. boundary reset or navigation) so
            // the details don't outlive the error UI that opened them.
            errorDetailsModal.open({ message: getErrorMessage(error) }, { shouldCloseOnUnmount: true });
          }}
        >
          Show details
        </Link>
      </Flex>
    </Box>
  );
};
